import React, { lazy, Suspense, useEffect, useContext, useRef, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { UserProvider } from "./context/UserContext";
import { ClientUserProvider } from "./context/ClientUserContext";
import { Provider } from "./components/ui/provider";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import { Toaster, toaster } from './components/Toaster';
import clevertapService from "./services/clevertapService";
import { fetchTokenSession } from './utils/apiUtils';
import { UserContext } from './context/UserContext';
import { ClientUserContext } from './context/ClientUserContext';

// Lazy load all components (keep alphabetically grouped for readability)
const SignUp = lazy(() => import("./components/SignUp"));
const Login = lazy(() => import("./components/Login"));
const Welcome = lazy(() => import("./components/Welcome"));
const VerifyOTP = lazy(() => import("./components/VerifyOTP"));
const ProcessedResponses = lazy(() => import("./components/ProcessedResponses"));
const Homepage = lazy(() => import("./components/Homepage"));
const CvUploadForm = lazy(() => import("./components/CvUpload"));
const CvUploadProgress = lazy(() => import("./components/CvUploadProgress"));
const UserProfile = lazy(() => import("./components/UserProfile"));
const LandingPage = lazy(() => import("./components/LandingPage"));
const InterviewHR = lazy(() => import("./components/InterviewHR"));
const AudioUploadForm = lazy(() => import("./components/AudioUpload"));
const ThankYouPage = lazy(() => import("./components/Thankyou"));
const JobListingPage = lazy(() => import("./components/JobListings"));
const JobDetailsPage = lazy(() => import("./components/JobDetailsPage"));
const HistoricalScores = lazy(() => import("./components/HistoricalScores"));
const ClientLogin = lazy(() => import("./components/ClientLogin"));
const ClientVerifyOTP = lazy(() => import("./components/ClientVerifyOtp"));
const ClientDashboard = lazy(() => import("./components/ClientDashboard"));
const JobDetails = lazy(() => import("./components/JobDetails"));
const ManageQuestions = lazy(() => import("./components/ManageQuestions"));
const InterviewList = lazy(() => import("./components/ClientInterviewList"));
const JobApplicantReports = lazy(() => import("./components/JobApplicantReports"));
const InviteUserJob = lazy(() => import("./components/InviteUserJob"));
const CreateClientJob = lazy(() => import("./components/CreateClientJob"));
const EditClientJob = lazy(() => import("./components/EditClientJob"));
const CreateInterviews = lazy(() => import("./components/CreateInterviews"));
const EditInterview = lazy(() => import("./components/EditInterview"));
const CreateInstructions = lazy(() => import("./components/CreateInstructions"));
const EditEvaluation = lazy(() => import("./components/EditEvaluation"));
const CreateQuestions = lazy(() => import("./components/CreateQuestions"));
const JobCandidatesList = lazy(() => import("./components/JobCandidatesList"));
const JobApply = lazy(() => import("./components/JobApply.js"));
const CandidateCvView = lazy(() => import("./components/CandidateCvView.js"));
const CandidateReport = lazy(() => import("./components/CandidateReport.js"));
const InvitedCandidateReport = lazy(() => import("./components/InvitedCandidateReport.js"));
const RejectedCvCandidateReport = lazy(() => import("./components/RejectedCvCandidateReport.js"));
const UserJobCandidateReport = lazy(() => import("./components/UserJobCandidateReport.js"));
const CandidateReportShared = lazy(() => import("./components/CandidateReportShared.js"));
const CreateEvaluationCriteria = lazy(() => import("./components/CreateEvaluationCriteria.js"));
const ClientEmailTemplates = lazy(() => import("./components/ClientEmailTemplates.js"));
const AdminEditTemplates = lazy(() => import("./components/AdminEditTemplates.js"));
const CvUploadSuccess = lazy(() => import("./components/CvUploadSuccess.js"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy.js"));
const TermsAndConditions = lazy(() => import("./components/TermsAndConditions.js"));
const RefundPolicy = lazy(() => import("./components/RefundPolicy.js"));
const ProductPricing = lazy(() => import("./components/ProductPricing.js"));
const TrainingUserDashboard = lazy(() => import("./components/TrainingUserDashboard.js"));
const TrainingUserReport = lazy(() => import("./components/TrainingUserReport.js"));

// Loading component for suspense & session checks
const LoadingFallback = () => (
  <Flex
    alignItems="center"
    justifyContent="center"
    flexDirection="column"
    height="100vh"
  >
    <Spinner size="xl" />
    <Text mt={4}>Loading...</Text>
  </Flex>
);

function App() {
  // Initialize CleverTap when app starts
  useEffect(() => {
    clevertapService.initialize();
  }, []);

  // Session reconciliation (only inside routing context) handled in a child so we can use navigation
  return (
    <Provider>
      <UserProvider>
        <ClientUserProvider>
          <Router>
            <SessionInitializer />
            <SessionExpiryToast />
            <Toaster />
            <div className="App">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/verify-otp" element={<VerifyOTP />} />
                  <Route path="/processed-responses" element={<ProcessedResponses />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={<Homepage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cv-upload-success" element={<CvUploadSuccess />} />
                  <Route path="/welcome" element={<Welcome />} />
                  <Route path="/cv-upload" element={<CvUploadForm />} />
                  <Route path="/cv-upload-progress" element={<CvUploadProgress />} />
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/user-home" element={<LandingPage />} />
                  <Route path="/jobs" element={<JobListingPage />} />
                  <Route path="/jobs/all-scores/:jobId/:userId" element={<HistoricalScores />} />
                  <Route path="/job-details/:jobId" element={<JobDetailsPage />} />
                  <Route path="/interviews/:jobId" element={<InterviewHR />} />
                  <Route path="/interview/:clientJobInterviewID" element={<AudioUploadForm />} />
                  <Route path="/thankyou" element={<ThankYouPage />} />
                  <Route path="/client-login" element={<ClientLogin />} />
                  <Route path="/client-verify-otp" element={<ClientVerifyOTP />} />
                  <Route path="/client-dashboard" element={<ClientDashboard />} />
                  <Route path="/client-jobs/:jobId" element={<JobDetails />} />
                  <Route path="/client-jobs/:jobId/interviews" element={<InterviewList />} />
                  <Route path="/manage-questions/:interviewId" element={<ManageQuestions />} />
                  <Route path="/client-jobs/:clientJobId/applicant-reports" element={<JobApplicantReports />} />
                  <Route path="/invite-user/job/:jobId" element={<InviteUserJob />} />
                  <Route path="/client-jobs/:clientJobId/candidates-list" element={<JobCandidatesList />} />
                  <Route path="/jobapply/:jobURL" element={<JobApply />} />
                  <Route path="/candidate/:userId/job/:jobId/cv" element={<CandidateCvView />} />
                  <Route path="/candidate-report/:jobId/invited-user/:invitedUserId" element={<InvitedCandidateReport />} />
                  <Route path="/candidate-report/:jobId/rejected-cv-user/:cvUploadJobSourcingId" element={<RejectedCvCandidateReport />} />
                  <Route path="/candidate-report/:jobId/user-job/:userId" element={<UserJobCandidateReport />} />
                  <Route path="/candidate-report/:jobId/:userId" element={<CandidateReport />} />
                  <Route path="/candidate-report-shared/:reportToken" element={<CandidateReportShared />} />
                  <Route path="/create-job" element={<CreateClientJob />} />
                  <Route path="/create-job/:jobId" element={<EditClientJob />} />
                  <Route path="/create-job/:jobId/interviews" element={<CreateInterviews />} />
                  <Route path="/create-job/:jobId/interviews/:interviewId" element={<EditInterview />} />
                  <Route path="/create-job/:jobId/interviews/:interviewId/instructions" element={<CreateInstructions />} />
                  <Route path="/create-job/:jobId/interviews/:clientInterviewId/evaluation" element={<EditEvaluation />} />
                  <Route path="/create-job/:jobId/interviews/:interviewId/questions" element={<CreateQuestions />} />
                  <Route path="/evaluation-criteria/create" element={<CreateEvaluationCriteria />} />
                  <Route path="/client-jobs/:jobId/settings/email" element={<ClientEmailTemplates />} />
                  <Route path="/admin/email-templates" element={<AdminEditTemplates />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/product-pricing" element={<ProductPricing />} />
                  <Route path="/training-client-dashboard" element={<TrainingUserDashboard />} />
                  <Route path="/client-training-users/:userId/report" element={<TrainingUserReport />} />
                </Routes>
              </Suspense>
            </div>
          </Router>
        </ClientUserProvider>
      </UserProvider>
    </Provider>
  );
}

// Separate component to run session initialization inside providers
const SessionInitializer = () => {
  const { user, setUser } = useContext(UserContext);
  const { clientUser, setClientUser } = useContext(ClientUserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const ranRef = useRef(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Shared report route: no auth required – don't block or redirect
    if (location.pathname.startsWith('/candidate-report-shared/')) {
      setChecking(false);
      return;
    }

    if (ranRef.current) return; // guard
    ranRef.current = true;

    (async () => {
      let session = null;
      try {
        session = await fetchTokenSession();
      } catch (err) {
        console.warn('Session fetch error (non-auth):', err);
      }

      const persistedUser = user || JSON.parse(localStorage.getItem('user') || 'null');
      const persistedClientUser = clientUser || JSON.parse(localStorage.getItem('clientUser') || 'null');

      if (!session) {
        // No active server session but we have a stale persisted user/clientUser
        if (persistedUser || persistedClientUser) {
          // Clear all user related persisted data
            localStorage.removeItem('user');
            localStorage.removeItem('clientUser');
            setUser(null);
            setClientUser(null);
          navigate('/', { replace: true, state: { sessionExpired: true } });
          setChecking(false);
          return;
        }
        setChecking(false);
        return;
      }

      // Session exists; hydrate if user not already in context
      if (session?.user && !persistedUser) {
        setUser(session.user);
      }
      if (session?.clientUser && !persistedClientUser) {
        setClientUser(session.clientUser);
      }
      setChecking(false);
    })();
  }, [navigate, setUser, setClientUser, user, clientUser, location.pathname]);

  if (checking) {
    return <LoadingFallback />; // block route render until done
  }
  return null;
};

export default App;

// Component to display a toast on session expiration when redirected to homepage
const SessionExpiryToast = () => {
  const location = useLocation();
  const handledRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (handledRef.current) return;
    if (location.state && location.state.sessionExpired) {
      handledRef.current = true;
      toaster.create({
        title: 'Session Expired',
        description: 'Your session has expired. Please login again.',
        type: 'warning',
        meta: { closable: true }
      });
      // Clear the state flag
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);
  return null;
};
